import { API, postJSON } from "../../api.js";
import { $, esc, fmtMoney } from "../../ui/format.js";
import { helpIcon } from "../../ui/help.js";
import { toast } from "../../ui/modals.js";

// ---------------------------------------------------------------- Competitor AI Engine

const STRATEGY_BLURB = {
  "Aggressive Discounter": "Wins share on price — expect price wars.",
  "Premium Innovator": "Competes on product — expect new launches.",
  "Fast Follower": "Copies what works, fast — mixed tactics.",
  "Scrappy Startup": "Cash-constrained, fundraises often.",
  "Efficient Operator": "Lean and consolidative — may acquire rivals.",
};

function _statusPillClass(status) {
  if (status === "Struggling") return "struggling";
  if (status === "Bankrupt") return "bankrupt";
  if (status === "Public") return "public";
  return "signed"; // Active — reuse the existing "sage" pill styling
}

// One line per reaction saying what it costs the player, read from the real
// numbers the engine recorded (growth headwind, morale, queued legal cost,
// price added to a deal, establishment delay) -- never a generic label.
function _reactionImpact(r) {
  const fx = r.effects || {};
  const parts = [];
  if (r.player_growth_delta) parts.push(`${(r.player_growth_delta * 100).toFixed(2)}%/mo growth`);
  if (fx.morale_delta) parts.push(`morale ${fx.morale_delta > 0 ? "+" : ""}${fx.morale_delta}`);
  if (fx.pending_cost) parts.push(`${fmtMoney(fx.pending_cost)} legal cost (next month's G&A)`);
  if (fx.integration_delayed_months) parts.push(`integration +${fx.integration_delayed_months} mo`);
  if (fx.new_price && fx.old_price) parts.push(`deal price +${fmtMoney(fx.new_price - fx.old_price)}`);
  if (fx.delay_months) parts.push(`establishment +${fx.delay_months} mo`);
  if (r.action === "snatch_target") parts.push("deal lost");
  return parts.length ? parts.join(" · ") : "—";
}

function renderCompetitorReactions(reactions) {
  const el = $("competitor-reactions");
  if (!el) return;
  const list = Array.isArray(reactions) ? reactions : [];
  if (!list.length) {
    el.innerHTML = `<p style="color:var(--text-mid)">No rival has reacted to your moves yet. Acquisitions, deals in progress and new countries can all provoke a response — usually a month or two later.</p>`;
    return;
  }
  el.innerHTML = `
    <table class="ledger">
      <tr><th>Month</th><th>Rival</th><th>Responding to</th><th>What they did</th><th>Cost to you</th></tr>
      ${list.map((r) => `
        <tr>
          <td class="mono">M${r.month}</td>
          <td class="label">${esc(r.competitor_name)}</td>
          <td>${esc(r.trigger_label)}</td>
          <td>${esc(r.description)}</td>
          <td class="mono neg">${esc(_reactionImpact(r))}</td>
        </tr>`).join("")}
    </table>`;
}

async function renderCompetitors(main, snap) {
  main.innerHTML = `
    <h2 class="panel-title">Competitor Intelligence — Month ${snap.month} ${helpIcon("competitors")}</h2>
    <div class="plain-english-card" id="competitors-plain-english">Loading…</div>

    <h3 style="font-family:var(--font-display); font-size:17px; margin:20px 0 10px;">World Ranking — Where You Stand</h3>
    <div id="world-ranking">Loading…</div>

    <h3 style="font-family:var(--font-display); font-size:17px; margin:28px 0 12px;">Rival Profiles</h3>
    <div id="competitor-cards">Loading…</div>
    <h3 style="font-family:var(--font-display); font-size:17px; margin:28px 0 12px;">How Rivals Have Responded to You</h3>
    <div id="competitor-reactions">Loading…</div>
    <h3 style="font-family:var(--font-display); font-size:17px; margin:28px 0 12px;">Recent Competitive Moves</h3>
    <div id="competitor-actions">Loading…</div>
  `;

  const [competitors, actions, ranking, reactions] = await Promise.all([
    fetch(`${API}/competitors`).then((x) => x.json()),
    fetch(`${API}/competitors/actions`).then((x) => x.json()),
    fetch(`${API}/world-ranking`).then((x) => x.json()),
    // A failure here must never take the whole tab down: reactions are additive.
    fetch(`${API}/competitors/reactions`).then((x) => x.json()).catch(() => []),
  ]);
  renderCompetitorReactions(reactions);

  const rankSuffix = (n) => { const s = ["th","st","nd","rd"]; const v = n % 100; return s[(v - 20) % 10] || s[v] || s[0]; };
  const maxRevenue = Math.max(1, ...ranking.entries.map((e) => e.revenue));
  $("world-ranking").innerHTML = `
    <div class="plain-english-card" style="margin-bottom:14px;">
      You rank <strong>#${ranking.rank} of ${ranking.total}</strong> by monthly revenue — the ${ranking.percentile}th percentile of the field.
      ${ranking.rank === 1 ? "You currently lead the market." : `${esc(ranking.entries[0].name)} leads at ${fmtMoney(ranking.entries[0].revenue)}/mo.`}
    </div>
    <table class="ledger">
      ${ranking.entries.map((e, i) => `
        <tr class="${e.is_player ? "total" : ""}">
          <td class="label" style="width:36px;">${i + 1}${rankSuffix(i + 1)}</td>
          <td class="label">${esc(e.name)}${e.is_player ? " (you)" : ""}${e.status === "Bankrupt" || e.status === "Struggling" ? ` <span style="color:var(--text-low); font-size:11px;">(${esc(e.status)})</span>` : ""}</td>
          <td class="mono" style="width:120px;">${fmtMoney(e.revenue)}/mo</td>
          <td style="width:160px;"><div class="share-bar-track"><div class="share-bar-fill" style="width:${Math.max(2, Math.round(100 * e.revenue / maxRevenue))}%"></div></div></td>
          <td class="mono" style="width:60px;">${e.market_share_pct}%</td>
        </tr>
      `).join("")}
    </table>
  `;

  const activeCompetitors = competitors.filter((c) => c.status !== "Bankrupt");
  const leader = [...activeCompetitors].sort((a, b) => b.market_share_pct - a.market_share_pct)[0];
  const movesThisMonth = actions.filter((a) => a.month === snap.month);
  $("competitors-plain-english").innerHTML = activeCompetitors.length
    ? `${activeCompetitors.length} active rival${activeCompetitors.length > 1 ? "s" : ""} in your market.
       ${leader ? `${esc(leader.name)} leads with ${leader.market_share_pct}% share (${esc(leader.strategy)}).` : ""}
       ${movesThisMonth.length ? `${movesThisMonth.length} move${movesThisMonth.length > 1 ? "s" : ""} happened this month —
       check below and consider a response.` : "No moves this month — quiet."}`
    : `No active rivals right now — either none have been seeded yet, or the field has thinned out.`;

  const maxShare = Math.max(1, ...competitors.map((c) => c.market_share_pct || 0));
  const cardsEl = $("competitor-cards");
  if (!competitors.length) {
    cardsEl.innerHTML = `<p style="color:var(--text-mid)">No competitor data yet — advance to month 1.</p>`;
  } else {
    cardsEl.innerHTML = competitors.map((c) => {
      const bankrupt = c.status === "Bankrupt";
      const barPct = Math.max(2, Math.round((c.market_share_pct / maxShare) * 100));
      return `
      <div class="competitor-card ${bankrupt ? "status-bankrupt" : ""}">
        <div class="competitor-card-top">
          <div class="competitor-name">${esc(c.name)}<span class="strategy-badge">${esc(c.strategy)}</span>${!bankrupt ? `<span class="strategy-badge" style="background:rgba(201,162,74,0.15); border-color:var(--brass); color:var(--brass);" title="${esc(c.archetype.tagline)}">${esc(c.archetype.label)}</span>` : ""}</div>
          <span class="status-pill ${_statusPillClass(c.status)}">${esc(c.status)}</span>
        </div>
        <div class="competitor-meta">${esc(c.funding_stage)} · Cash ${fmtMoney(c.cash)} · Est. revenue ${fmtMoney(c.monthly_revenue)}/mo · Reputation ${Math.round(c.reputation)}/100</div>
        <div class="competitor-meta" style="margin-bottom:2px;">Market share — ${c.market_share_pct}%</div>
        <div class="share-bar-track"><div class="share-bar-fill" style="width:${bankrupt ? 0 : barPct}%"></div></div>
        <div class="competitor-action-line ${c.last_action ? "" : "no-move"}">
          ${c.last_action ? esc(c.last_action) : "No moves logged yet."}
        </div>
        <div style="font-size:11.5px; color:var(--text-low); margin-top:4px;">${STRATEGY_BLURB[c.strategy] || ""}</div>
        ${bankrupt ? "" : `
        <div class="deal-actions" style="margin-top:12px;">
          <button data-respond="${esc(c.name)}" data-response="Price Match">Price Match</button>
          <button data-respond="${esc(c.name)}" data-response="Marketing Counter">Marketing Counter</button>
          <button data-respond="${esc(c.name)}" data-response="Ignore">Ignore</button>
        </div>`}
      </div>
    `;
    }).join("");

    cardsEl.querySelectorAll("[data-respond]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        const res = await postJSON("/competitors/respond", {
          competitor_name: btn.dataset.respond,
          response_type: btn.dataset.response,
        });
        await window.refreshAll();
        toast(res.message);
      });
    });
  }

  const actionsEl = $("competitor-actions");
  actionsEl.innerHTML = actions.length ? `
    <table class="ledger">
      <tr><th>Month</th><th>Rival</th><th>Move</th><th>Growth Impact</th></tr>
      ${actions.map((a) => `
        <tr>
          <td class="mono">M${a.month}</td>
          <td class="label">${esc(a.competitor_name)}</td>
          <td>${esc(a.move || a.description)}</td>
          <td class="mono ${a.player_growth_delta < 0 ? "neg" : a.player_growth_delta > 0 ? "pos" : ""}">
            ${a.player_growth_delta ? (a.player_growth_delta > 0 ? "+" : "") + (a.player_growth_delta * 100).toFixed(2) + "%/mo" : "—"}
          </td>
        </tr>
      `).join("")}
    </table>
  ` : `<p style="color:var(--text-mid)">No competitive moves logged yet.</p>`;
}

export { renderCompetitors };
